import React from "react"
import styled from "styled-components"
import { motion } from "framer-motion"
import { portfolio as t } from "../Themes"
import { Live, Enterprise, Profiles } from "../../data/WorkData"
import { Section, Inner, Label, Heading, reveal, revealDelay } from "./ui"

const Grid = styled.div`
display: grid;
grid-template-columns: repeat(2, 1fr);
gap: 1.2rem;
margin-top: 0.5rem;

@media (max-width: 52em){ grid-template-columns: 1fr; }
`

const Card = styled(motion.article)`
grid-column: ${(p) => (p.featured ? "1 / -1" : "auto")};
position: relative;
display: flex;
flex-direction: column;
border: 1px solid ${t.border};
border-radius: 18px;
padding: clamp(1.4rem, 3vw, 2rem);
background: ${t.surface};
overflow: hidden;
transition: border-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;

&::before{
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(120% 120% at 0% 0%, rgba(${t.accentRgba}, 0.08), transparent 45%);
    opacity: 0;
    transition: opacity 0.3s ease;
}

&:hover{
    transform: translateY(-4px);
    border-color: ${t.borderHi};
    box-shadow: 0 24px 50px -28px rgba(0,0,0,0.8);
}
&:hover::before{ opacity: 1; }
`

const Top = styled.div`
display: flex;
align-items: center;
justify-content: space-between;
margin-bottom: 1rem;
position: relative;
z-index: 1;
`

const Tag0 = styled.span`
font-family: ${t.mono};
font-size: 0.76rem;
color: ${(p) => (p.muted ? t.faint : t.accent)};
letter-spacing: 0.04em;
display: inline-flex;
align-items: center;
gap: 0.4rem;
`

const VisitLink = styled.a`
display: inline-flex;
align-items: center;
gap: 0.4rem;
font-family: ${t.mono};
font-size: 0.8rem;
color: ${t.muted};
transition: color 0.2s ease;

&:hover{ color: ${t.accent}; }
svg{ transition: transform 0.2s ease; }
&:hover svg{ transform: translate(2px, -2px); }
`

const Name = styled.h3`
font-size: ${(p) => (p.featured ? "clamp(1.4rem, 3vw, 2rem)" : "clamp(1.15rem, 2.4vw, 1.4rem)")};
margin-bottom: 0.6rem;
position: relative;
z-index: 1;
`

const Desc = styled.p`
color: ${t.muted};
line-height: 1.7;
max-width: ${(p) => (p.featured ? "70ch" : "100%")};
margin-bottom: 1.3rem;
flex: 1;
position: relative;
z-index: 1;
`

const Tags = styled.div`
display: flex;
flex-wrap: wrap;
gap: 0.5rem;
position: relative;
z-index: 1;
`

const Tag = styled.span`
font-family: ${t.mono};
font-size: 0.72rem;
color: ${t.faint};
`

const SubLabel = styled(motion.div)`
display: flex;
align-items: baseline;
gap: 0.8rem;
flex-wrap: wrap;
margin: 4rem 0 1.5rem;

h3{ font-size: clamp(1.2rem, 2.4vw, 1.6rem); }
span{
    font-family: ${t.mono};
    font-size: 0.8rem;
    color: ${t.faint};
}
`

const Profiless = styled(motion.div)`
display: flex;
align-items: center;
flex-wrap: wrap;
gap: 1rem;
margin-top: 2.4rem;
font-family: ${t.mono};
font-size: 0.85rem;
color: ${t.muted};

a{
    color: ${t.accent};
    border-bottom: 1px solid rgba(${t.accentRgba}, 0.4);
    transition: opacity 0.2s ease;
}
a:hover{ opacity: 0.75; }
`

const Arrow = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M7 17 17 7" />
        <path d="M7 7h10v10" />
    </svg>
)

const Lock = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
)

const Projects = () => {
    const featured = Enterprise.find((p) => p.featured)
    const enterpriseRest = Enterprise.filter((p) => !p.featured)

    return (
        <Section id="work">
            <Inner>
                <Label num="04" {...reveal}>Selected Work</Label>
                <Heading {...reveal}>Platforms &amp; products I've shipped.</Heading>

                <Grid>
                    {featured && (
                        <Card featured={1} {...reveal}>
                            <Top>
                                <Tag0>★ Featured · Flagship</Tag0>
                                <Tag0 muted={1}><Lock /> Private</Tag0>
                            </Top>
                            <Name featured={1}>{featured.name}</Name>
                            <Desc featured={1}>{featured.description}</Desc>
                            <Tags>
                                {featured.tags.map((tag) => (
                                    <Tag key={tag}>#{tag}</Tag>
                                ))}
                            </Tags>
                        </Card>
                    )}

                    {Live.map((p, i) => (
                        <Card key={p.id} {...revealDelay(i)}>
                            <Top>
                                <Tag0>Live</Tag0>
                                <VisitLink href={p.url} target="_blank" rel="noreferrer">
                                    Visit <Arrow />
                                </VisitLink>
                            </Top>
                            <Name>{p.name}</Name>
                            <Desc>{p.description}</Desc>
                            <Tags>
                                {p.tags.map((tag) => (
                                    <Tag key={tag}>#{tag}</Tag>
                                ))}
                            </Tags>
                        </Card>
                    ))}
                </Grid>

                <SubLabel {...reveal}>
                    <h3>Enterprise &amp; private work</h3>
                    <span>delivered through private company repositories</span>
                </SubLabel>

                <Grid>
                    {enterpriseRest.map((p, i) => (
                        <Card key={p.id} {...revealDelay(i)}>
                            <Top>
                                <Tag0 muted={1}><Lock /> Private</Tag0>
                            </Top>
                            <Name>{p.name}</Name>
                            <Desc>{p.description}</Desc>
                            <Tags>
                                {p.tags.map((tag) => (
                                    <Tag key={tag}>#{tag}</Tag>
                                ))}
                            </Tags>
                        </Card>
                    ))}
                </Grid>

                <Profiless {...reveal}>
                    More of my work:
                    {Profiles.map((pr) => (
                        <a key={pr.label} href={pr.url} target="_blank" rel="noreferrer">
                            {pr.label}
                        </a>
                    ))}
                </Profiless>
            </Inner>
        </Section>
    )
}

export default Projects
